import React, { ChangeEvent, RefObject, useState } from 'react';
import { ArrowRightIcon, CheckCircle2Icon, ClockIcon, FileUpIcon, ShieldCheckIcon } from 'lucide-react';
import { CarliChat } from './CarliChat';
import { activePrompts, activityFor, attentionFor, onboardingPrompts, recommendationFor } from '../data/homeSignals';
export type PortalView = 'carli' | 'inventory' | 'performance' | 'marketing' | 'controls' | 'messages' | 'finances' | 'demand' | 'pipeline';
type QuickStats = {
  received: string;
  sold: string;
  sales: string;
  live: string;
  liveStyles?: string;
  maxOpportunity: string;
  msrp?: string;
  salesDelta?: string | null;
  unitsDelta?: string | null;
  forecastSales?: string | null;
  forecastDetail?: string | null;
};
type HomeDashboardProps = {
  selectedOwner: string;
  quickStats: QuickStats;
  hasInventory: boolean;
  shopifyConnected: boolean;
  onConnectShopify: () => void;
  fileInputRef: RefObject<HTMLInputElement>;
  onFileChange: (event: ChangeEvent<HTMLInputElement>) => void;
  fileName: string | null;
  onRunAnalysis: () => void;
  onNavigate: (view: PortalView) => void;
  latestReport?: {
    name: string;
    onOpen: () => void;
  };
};
export function HomeDashboard({
  selectedOwner,
  quickStats,
  hasInventory,
  shopifyConnected,
  onConnectShopify,
  fileInputRef,
  onFileChange,
  fileName,
  onRunAnalysis,
  onNavigate,
  latestReport
}: HomeDashboardProps) {
  const [analysisStarted, setAnalysisStarted] = useState(false);
  const attention = hasInventory ? attentionFor(selectedOwner) : [];
  const recommendation = hasInventory ? recommendationFor(selectedOwner) : null;
  const activity = hasInventory ? activityFor(selectedOwner) : [];
  return <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8 lg:py-14" data-id="element-906" data-mp="14pmvigjsm7zg">
      <section className="pb-8" aria-labelledby="home-heading" data-id="element-907" data-mp="znnwtjyzzz8u">
        <div className="flex flex-wrap items-end justify-between gap-x-8 gap-y-4" data-id="element-908" data-mp="3peapjxdohsx8">
          <div data-id="element-909" data-mp="3key2pcmt4lun">
            <CarliLabel data-id="element-910" data-mp="3ke51er6maojw" />
            <h1 id="home-heading" className="mt-3 font-display text-4xl font-medium tracking-tight sm:text-5xl" data-id="element-911" data-mp="3kdws199pe1dg">
              {hasInventory ? 'Your recovery overview' : 'Welcome to Carli'}
            </h1>
            <p className="mt-3 max-w-2xl text-lg leading-relaxed text-ink/65" data-id="element-912" data-mp="3queqez14emty">
              {hasInventory ? 'See what’s happening, what Carli recommends, and what needs your attention.' : 'Start by adding inventory. Carli will evaluate the opportunity and recommend the best recovery path.'}
            </p>
          </div>
          {hasInventory && latestReport && <button type="button" onClick={latestReport.onOpen} className="inline-flex items-center gap-1.5 text-sm font-medium text-forest hover:underline" data-id="element-913" data-mp="37xn9gw91wjt2">
              Latest report: {latestReport.name}
              <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-914" data-mp="2y6iv9yr792za" />
            </button>}
        </div>

        {hasInventory && <dl className="mt-8 grid grid-cols-2 gap-x-8 gap-y-7 border-t border-ink/10 pt-7 lg:grid-cols-4" data-id="element-915" data-mp="2p6480tuoddt5">
            <HeadlineStat label="Total sales" value={quickStats.sales} detail={quickStats.salesDelta ?? 'Gross recovery revenue'} data-id="element-916" data-mp="25izppkxdh0xe" />
            <HeadlineStat label="Units sold" value={quickStats.sold} detail={quickStats.unitsDelta ?? 'Across all recovery channels'} data-id="element-917" data-mp="20m9bz491ihf5" />
            <HeadlineStat label="Live units" value={quickStats.live} detail={`Across ${quickStats.liveStyles ?? '0'} styles`} data-id="element-918" data-mp="2aivsxhy8wobw" />
            <HeadlineStat label="Est. next 7 days" value={quickStats.forecastSales ?? '—'} detail={quickStats.forecastDetail ?? 'Projected recovery sales'} data-id="element-919" data-mp="2ohvfmk276t5f" />
          </dl>}
      </section>

      {hasInventory ? <>
          <AttentionSection items={attention} liveUnits={quickStats.live} onNavigate={onNavigate} data-id="element-920" data-mp="2t8u1dz5ygjg2" />

          {recommendation && <section className="mt-6 overflow-hidden rounded-3xl bg-ink text-cream shadow-sm" aria-labelledby="recommendation-heading" data-id="element-921" data-mp="379o9sht06keq">
              <div className="grid gap-6 p-6 sm:p-9 lg:grid-cols-[1.4fr_0.6fr] lg:items-end" data-id="element-922" data-mp="335qd0dr5hlou">
                <div data-id="element-923" data-mp="10j1pet1gfkdq">
                  <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-cream/55" data-id="element-924" data-mp="15jpdp6yatn49">
                    <CarliGlyph tone="cream" data-id="element-925" data-mp="1ac0fyggihkzz" />
                    Carli recommends
                  </span>
                  <h2 id="recommendation-heading" className="mt-4 max-w-2xl font-display text-3xl font-medium tracking-tight sm:text-4xl" data-id="element-926" data-mp="1f8h6tif3idgr">
                    {recommendation.title}
                  </h2>
                  <p className="mt-3 max-w-2xl text-sm leading-relaxed text-cream/65" data-id="element-927" data-mp="mlmvpc464qfy">{recommendation.observation}</p>
                  <p className="mt-2 max-w-2xl text-sm leading-relaxed text-cream/75" data-id="element-928" data-mp="rmaoa281pfca">{recommendation.reasoning}</p>
                </div>
                <div className="lg:text-right" data-id="element-929" data-mp="22zw95b6vgus0">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-cream/50" data-id="element-930" data-mp="22z3cj31mnlnl">Expected impact</p>
                  <p className="mt-2 font-display text-3xl font-semibold tracking-tight" data-id="element-931" data-mp="272sxzjn09n4e">{recommendation.impact}</p>
                  <button type="button" onClick={() => onNavigate(recommendation.target)} className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#BB5E40] px-5 py-3 text-sm font-semibold text-cream transition-colors hover:bg-[#94452E]" data-id="element-932" data-mp="3nhds6lnwytkj">
                    {recommendation.action}
                    <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-933" data-mp="38tstd3y2ngif" />
                  </button>
                </div>
              </div>
            </section>}
        </> : <section className="mt-8" aria-labelledby="getting-started-heading" data-id="element-934" data-mp="2zs30dtojk9ij">
          <h2 id="getting-started-heading" className="sr-only" data-id="element-935" data-mp="2uu1hp9v12c7l">Getting started</h2>
          <ol className="grid gap-4 lg:grid-cols-3" data-id="element-936" data-mp="y0pv33invzly">
            <Step number="1" title="Add inventory" text="Upload a spreadsheet or connect Shopify." data-id="element-937" data-mp="xy3sliamoaal" />
            <Step number="2" title="Set your recovery rules" text="Choose approved channels, pricing boundaries, and timing." data-id="element-938" data-mp="szir7fjn3kkk" />
            <Step number="3" title="Review Carli’s recommendation" text="Carli will estimate recovery and recommend a recovery strategy." data-id="element-939" data-mp="1gx8c5xe1b113" />
          </ol>
        </section>}

      <section className="mt-6 grid gap-5 lg:grid-cols-[1.05fr_0.95fr]" aria-labelledby="add-inventory-heading" data-id="element-940" data-mp="184ehosq0nv2n">
        <article className="rounded-3xl border border-ink/10 bg-paper p-6 shadow-sm sm:p-8" data-id="element-941" data-mp="1vu8ujaac5jmw">
          <h2 id="add-inventory-heading" className="font-display text-2xl font-medium tracking-tight sm:text-3xl" data-id="element-942" data-mp="1zvartlkozlm8">Add inventory</h2>
          <p className="mt-2 max-w-md text-sm leading-relaxed text-ink/65" data-id="element-943" data-mp="1luqcizdzk7xh">Give Carli new inventory to evaluate and recover.</p>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row" data-id="element-944" data-mp="1qt1mda4a6ifp">
            <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" onChange={onFileChange} className="sr-only" aria-label="Upload inventory file" data-id="element-945" data-mp="afl3zer6bf5y" />
            <button type="button" onClick={() => fileInputRef.current?.click()} className="inline-flex items-center justify-center gap-2 rounded-full bg-forest px-5 py-3.5 text-sm font-semibold text-cream transition-colors hover:bg-forest-dark" data-id="element-946" data-mp="7w6m9ra9oa30">
              <FileUpIcon className="h-4 w-4" aria-hidden="true" data-id="element-947" data-mp="bxs5rvhb3mm6" />
              Upload inventory
            </button>
            <button type="button" onClick={onConnectShopify} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#95BF47] px-5 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#7FAE3A]" data-id="element-948" data-mp="3kd3huqetndk8">
              {shopifyConnected ? <CheckCircle2Icon className="h-4 w-4" aria-hidden="true" data-id="element-949" data-mp="3pc6g9s0aqlj8" /> : <ShopifyGlyph data-id="element-950" data-mp="3pav9rkfguzof" />}
              {shopifyConnected ? 'Shopify connected' : 'Connect Shopify'}
            </button>
          </div>

          {fileName && <div className="mt-4 flex items-center gap-2 rounded-xl border border-forest/20 bg-forest/5 px-4 py-3 text-sm text-ink/70" role="status" data-id="element-951" data-mp="2wnhl09lx9afe">
              <CheckCircle2Icon className="h-4 w-4 shrink-0 text-forest" aria-hidden="true" data-id="element-952" data-mp="2g3iwrc0zqpz0" />
              <span data-id="element-953" data-mp="2l2vkdyzos12n"><strong className="font-semibold text-ink" data-id="element-954" data-mp="2l35b5osk2c9e">{fileName}</strong> is ready to add to your recovery workspace.</span>
            </div>}

          <p className="mt-6 flex items-start gap-2 border-t border-ink/10 pt-5 text-xs leading-relaxed text-ink/50" data-id="element-955" data-mp="2q39t7j943f1q">
            <ShieldCheckIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-forest" aria-hidden="true" data-id="element-956" data-mp="2uz8ntol19tal" />
            Nothing is activated without your approval. ERP and 3PL connections are coming next.
          </p>
        </article>

        <div className="overflow-hidden rounded-3xl shadow-sm" data-id="element-957" data-mp="38szm20tbdqr0">
          <CarliChat prompts={hasInventory ? activePrompts : onboardingPrompts} subheading="Ask about your inventory, recovery strategy, performance, or what to do next." context={carliContext(selectedOwner, quickStats, hasInventory)} data-id="element-958" data-mp="38ukenkw2izm8" />
        </div>
      </section>

      <section className="mt-5 rounded-3xl border border-[#BB5E40]/25 bg-[#BB5E40]/5 p-5 sm:p-7" aria-labelledby="test-inventory-heading" data-id="element-959" data-mp="1247dn0v278hy">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between" data-id="element-960" data-mp="x65uyh1jpb70">
          <div data-id="element-961" data-mp="s6tc07dsoo9p">
            <h2 id="test-inventory-heading" className="text-xl font-semibold tracking-tight" data-id="element-962" data-mp="s7bdbifbdsoh">Test inventory with Carli</h2>
            <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-ink/65" data-id="element-963" data-mp="1pt0glha3mgm2">
              Explore the recovery opportunity before adding inventory to your account. Upload a spreadsheet and Carli will estimate potential recovery, likely channels, and expected timing.
            </p>
            <p className="mt-1.5 text-xs text-ink/50" data-id="element-964" data-mp="1b4cjv7oio0uj">Nothing will be added to your live inventory or activated without your approval.</p>
          </div>
          <button type="button" onClick={() => {
          setAnalysisStarted(true);
          onRunAnalysis();
        }} className={`inline-flex shrink-0 items-center justify-center gap-2 rounded-full border px-5 py-3 text-sm font-semibold transition-colors ${analysisStarted ? 'border-[#BB5E40]/35 bg-paper text-[#94452E] hover:bg-cream' : 'border-[#BB5E40] bg-[#BB5E40] text-cream hover:bg-[#94452E]'}`} data-id="element-965" data-mp="23t1ecnv2h7xb">
            Run Carli Analysis
          </button>
        </div>
      </section>

      {activity.length > 0 && <section className="mt-5 rounded-3xl border border-ink/10 bg-paper p-5 sm:p-7" aria-labelledby="activity-heading" data-id="element-966" data-mp="umpyh8520x1p">
          <div className="flex items-center justify-between gap-4" data-id="element-967" data-mp="18gqlx5qk2xmr">
            <h2 id="activity-heading" className="flex items-center gap-2 text-base font-semibold" data-id="element-968" data-mp="13guip8pona89">
              <ClockIcon className="h-4 w-4 text-[#BB5E40]" aria-hidden="true" data-id="element-969" data-mp="1idlmrvaaxa47" />
              Recent activity
            </h2>
            <button type="button" onClick={() => onNavigate('performance')} className="text-sm font-medium text-forest hover:underline" data-id="element-970" data-mp="1dbmimj6qm967">
              View all activity
            </button>
          </div>
          <ul className="mt-4 divide-y divide-ink/10" data-id="element-971" data-mp="20yrs8dc8mrqf">
            {activity.map(entry => <li key={`${entry.when}-${entry.detail}`} className="flex flex-col gap-0.5 py-3 sm:flex-row sm:items-baseline sm:gap-6" data-id="element-972" data-mp="1n3fpbinxhuyf">
                <span className="w-24 shrink-0 text-xs font-semibold uppercase tracking-wider text-ink/45" data-id="element-973" data-mp="1db03295lmk7q">{entry.when}</span>
                <span className="text-sm text-ink/75" data-id="element-974" data-mp="1i31pgnwhdrbh">{entry.detail}</span>
              </li>)}
          </ul>
        </section>}
    </div>;
}
function AttentionSection({
  items,
  liveUnits,
  onNavigate
}: {
  items: ReturnType<typeof attentionFor>;
  liveUnits: string;
  onNavigate: (view: PortalView) => void;
}) {
  if (items.length === 0) {
    return <section className="mt-8 rounded-3xl border border-forest/20 bg-forest/5 p-5 sm:p-7" aria-labelledby="attention-heading" data-id="element-975" data-mp="zca74wuimqky">
        <div className="flex items-start gap-3" data-id="element-976" data-mp="g7xb6q8ppixp">
          <CheckCircle2Icon className="mt-0.5 h-5 w-5 shrink-0 text-forest" aria-hidden="true" data-id="element-977" data-mp="bgfa826ovicq" />
          <div data-id="element-978" data-mp="6hc2ub38l01l">
            <h2 id="attention-heading" className="text-lg font-semibold" data-id="element-979" data-mp="6gsvfob7pu1t">You’re all caught up</h2>
            <p className="mt-1 text-sm leading-relaxed text-ink/65" data-id="element-980" data-mp="1ihi33lpl1eu">
              Carli is actively managing {liveUnits} units across your approved recovery channels.
            </p>
          </div>
        </div>
      </section>;
  }
  return <section className="mt-8" aria-labelledby="attention-heading" data-id="element-981" data-mp="u7g4we0s0rqh">
      <div className="flex items-center gap-2.5" data-id="element-982" data-mp="p70t3qw57sam">
        <span aria-hidden="true" className="h-2 w-2 rounded-full bg-[#BB5E40]" data-id="element-983" data-mp="p9wp39ofk6wf" />
        <h2 id="attention-heading" className="text-lg font-semibold" data-id="element-984" data-mp="k8h6do9q6ch7">Needs your attention</h2>
        <span className="rounded-full bg-[#BB5E40]/12 px-2.5 py-0.5 text-xs font-semibold text-[#94452E]" data-id="element-985" data-mp="39zm3xmp4vsp4">
          {items.length} {items.length === 1 ? 'item' : 'items'}
        </span>
      </div>

      <ul className="mt-3 space-y-3" data-id="element-986" data-mp="1kiiaeajnerrh">
        {items.map(item => <li key={item.id} data-id="element-987" data-mp="1pf8k8g4v0r4g">
            <article className="flex flex-col gap-4 rounded-2xl border border-[#BB5E40]/25 bg-paper p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6" data-id="element-988" data-mp="1pgjtgvh2qr2y">
              <div data-id="element-989" data-mp="1z63arwp52cvp">
                <h3 className="text-base font-semibold" data-id="element-990" data-mp="1z6lgrl1ls5gt">{item.title}</h3>
                <p className="mt-1 max-w-2xl text-sm leading-relaxed text-ink/65" data-id="element-991" data-mp="245fx8gc3lhxr">{item.detail}</p>
                {item.impact && <p className="mt-2 text-sm font-semibold text-forest" data-id="element-992" data-mp="285gm6uwtpbj2">{item.impact}</p>}
              </div>
              <button type="button" onClick={() => onNavigate(item.target)} className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-[#BB5E40] px-4 py-2.5 text-sm font-semibold text-cream transition-colors hover:bg-[#94452E]" data-id="element-993" data-mp="2d6w78mz03i1g">
                {item.action}
              </button>
            </article>
          </li>)}
      </ul>
    </section>;
}
function HeadlineStat({
  label,
  value,
  detail
}: {
  label: string;
  value: string;
  detail: string;
}) {
  return <div data-id="element-994" data-mp="2n8gbziewqxsj">
      <dt className="text-xs font-semibold uppercase tracking-wider text-ink/45" data-id="element-995" data-mp="2n86pw5wzhas4">{label}</dt>
      <dd data-id="element-996" data-mp="2j74lzbs4gajt">
        <p className="mt-2 font-display text-4xl font-semibold tracking-tight text-ink sm:text-5xl" data-id="element-997" data-mp="2j6boz2iyszev">{value}</p>
        <p className="mt-1.5 text-[13px] text-ink/55" data-id="element-998" data-mp="1vghe2qi7hkx9">{detail}</p>
      </dd>
    </div>;
}
function Step({
  number,
  title,
  text
}: {
  number: string;
  title: string;
  text: string;
}) {
  return <li className="rounded-2xl border border-ink/10 bg-paper p-5 sm:p-6" data-id="element-999" data-mp="249ucu92ybgr9">
      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#BB5E40]/12 font-display text-sm font-semibold text-[#94452E]" data-id="element-1000" data-mp="1zivvd6688cmf">{number}</span>
      <h3 className="mt-4 text-base font-semibold" data-id="element-1001" data-mp="1brqfir0adkc4">{title}</h3>
      <p className="mt-1.5 text-sm leading-relaxed text-ink/65" data-id="element-1002" data-mp="17shlrm58xmgk">{text}</p>
    </li>;
}
function CarliLabel() {
  return <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-1003" data-mp="xmzlhxov26id">
      <CarliGlyph data-id="element-1004" data-mp="12fspgjci9ate" />
      Carli
    </span>;
}
function CarliGlyph({
  tone = 'coral'
}: {
  tone?: 'coral' | 'cream';
}) {
  return <span aria-hidden="true" className={`h-4 w-4 shrink-0 ${tone === 'cream' ? 'bg-cream' : 'bg-[#BB5E40]'}`} style={{
    WebkitMaskImage: 'url(https://cdn.magicpatterns.com/uploads/1TE2x8VkDnCcShd3sjGJpW/carli_icon.svg)',
    maskImage: 'url(https://cdn.magicpatterns.com/uploads/1TE2x8VkDnCcShd3sjGJpW/carli_icon.svg)',
    WebkitMaskRepeat: 'no-repeat',
    maskRepeat: 'no-repeat',
    WebkitMaskPosition: 'center',
    maskPosition: 'center',
    WebkitMaskSize: 'contain',
    maskSize: 'contain'
  }} data-id="element-1005" data-mp="soob9lunltzm" />;
}
function ShopifyGlyph() {
  return <span aria-hidden="true" className="h-4 w-4 shrink-0 bg-cream" style={{
    WebkitMaskImage: 'url(https://cdn.magicpatterns.com/uploads/7LeZM2476qhffRRTPqij7m/shopify_glyph_black.svg)',
    maskImage: 'url(https://cdn.magicpatterns.com/uploads/7LeZM2476qhffRRTPqij7m/shopify_glyph_black.svg)',
    WebkitMaskRepeat: 'no-repeat',
    maskRepeat: 'no-repeat',
    WebkitMaskPosition: 'center',
    maskPosition: 'center',
    WebkitMaskSize: 'contain',
    maskSize: 'contain'
  }} data-id="element-1006" data-mp="ioktk1hux5" />;
}
function carliContext(brand: string, stats: QuickStats, hasInventory: boolean) {
  if (!hasInventory) {
    return 'The brand is signed into the Manymoons recovery portal but has not added inventory yet. Help them understand what to send first, what data Carli needs, how recovery pricing works, and which channels might fit. Keep answers to two short paragraphs at most.';
  }
  return [`The brand is ${brand || 'a Manymoons brand'}, signed into their recovery portal.`, `Current position: ${stats.live} active units, ${stats.sold} units sold, ${stats.sales} recovery generated, ${stats.maxOpportunity} of remaining opportunity across ${stats.received} units received.`, 'Answer as their recovery analyst using these numbers. Be specific and commercial. Two short paragraphs at most.'].join(' ');
}